/**
 * Single Day Strategy
 *
 * Handles single date selection logic for booking functionality.
 * Implements booking-specific validation rules and constraints.
 *
 * Following SOLID principles:
 * - Single Responsibility: Only handles single date selection logic
 * - Open/Closed: Can be extended for multi-day trips without modification
 * - Strategy Pattern: Interchangeable with other date selection strategies
 *
 * Part of: Booking Calendar Migration (Phase 1)
 * Location: apps/web/src/components/calendar/strategies/SingleDayStrategy.js
 * Created: 2025-11-01
 */

class SingleDayStrategy {
  constructor({
    minBookingHours = 48,      // Minimum advance notice (default: 48 hours)
    bookingHorizonMonths = 12, // Maximum months ahead (default: 12 months)
    tripStartTime = '06:00:00', // Default trip start time (6 AM)
    startDayRestrictions = null // { allowed_days: [0-6] } or null/empty for no restrictions
  } = {}) {
    this.minBookingHours = minBookingHours;
    this.bookingHorizonMonths = bookingHorizonMonths;
    this.tripStartTime = tripStartTime;
    this.startDayRestrictions = startDayRestrictions;

    // Parse trip start time
    const [hours, minutes, seconds] = this.tripStartTime.split(':').map(Number);
    this.startHour = hours;
    this.startMinute = minutes || 0;
    this.startSecond = seconds || 0;
  }

  /**
   * Process a date selection
   *
   * For single-day booking, always replaces the previous selection.
   * This allows users to change their mind easily.
   *
   * @param {Date} date - The date being selected
   * @param {Date[]} currentSelection - Currently selected dates (ignored for single-day)
   * @returns {Date[]} New selection array with single date
   */
  selectDate(date, _currentSelection) {
    // Always replace selection with new date
    return [date];
  }

  /**
   * Check if selection is complete (has one date)
   *
   * @param {Date[]} selection - Current selection
   * @returns {boolean} True if exactly one date is selected
   */
  isSelectionComplete(selection) {
    return selection.length === 1;
  }

  /**
   * Validate the selection against booking rules
   *
   * Booking-specific validation rules:
   * 1. Date is not in the past
   * 2. Date meets minimum advance booking requirement (e.g., 48 hours from trip start time)
   * 3. Date is within booking horizon (e.g., 12 months ahead)
   *
   * @param {Date[]} selection - Dates to validate
   * @returns {Object} Validation result { isValid: boolean, error?: string }
   */
  validate(selection) {
    if (selection.length === 0) {
      return { isValid: false, error: 'Please select a date' };
    }

    const selectedDate = selection[0];
    const now = new Date();

    // Rule 1: Cannot book past dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(selectedDate);
    checkDate.setHours(0, 0, 0, 0);

    if (checkDate < today) {
      return { isValid: false, error: 'Cannot select past dates' };
    }

    // Rule 2: Must meet minimum advance booking requirement
    // Calculate from the actual trip start time (e.g., 6 AM), not midnight
    const tripDateTime = new Date(selectedDate);
    tripDateTime.setHours(this.startHour, this.startMinute, this.startSecond, 0);

    const hoursUntilTrip = (tripDateTime - now) / (1000 * 60 * 60);
    if (hoursUntilTrip < this.minBookingHours) {
      return {
        isValid: false,
        error: `Minimum ${this.minBookingHours} hours advance notice required`
      };
    }

    // Rule 3: Must be within booking horizon
    const maxBookingDate = new Date(now);
    maxBookingDate.setMonth(maxBookingDate.getMonth() + this.bookingHorizonMonths);

    if (selectedDate > maxBookingDate) {
      return {
        isValid: false,
        error: `Cannot book more than ${this.bookingHorizonMonths} months in advance`
      };
    }

    return { isValid: true };
  }

  /**
   * Get human-readable display of selection
   *
   * For booking context, shows full date with day of week.
   * Example: "Wednesday, November 15, 2025"
   *
   * @param {Date[]} selection - Selected dates
   * @returns {string} Formatted display string
   */
  getSelectionDisplay(selection) {
    if (selection.length === 0) {
      return 'Select a date';
    }

    return this.formatDate(selection[0]);
  }

  /**
   * Get the selectable date range based on current selection
   *
   * For single-day booking, there are no range constraints.
   * However, we return the booking constraints for the calendar to enforce.
   *
   * @param {Date[]} selection - Current selection (unused for single-day)
   * @returns {Object|null} Range constraints
   */
  getSelectableRange(_selection) {
    // Return booking constraints
    const now = new Date();
    const minDate = this.getMinBookingDate();
    const maxDate = new Date(now);
    maxDate.setMonth(maxDate.getMonth() + this.bookingHorizonMonths);

    return {
      minDate,
      maxDate
    };
  }

  /**
   * Check if a date is within the selected range (for styling)
   *
   * For single-day booking, there is no range.
   * This always returns false.
   *
   * @param {Date} date - Date to check
   * @param {Date[]} selection - Current selection
   * @returns {boolean} Always false for single-day
   */
  isDateInRange(_date, _selection) {
    return false; // No range for single day selection
  }

  /**
   * Format a date for display in booking context
   *
   * Shows full date with day of week for clarity.
   * Example: "Wednesday, November 15, 2025"
   *
   * @private
   * @param {Date} date - Date to format
   * @returns {string} Formatted date string
   */
  formatDate(date) {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }

  /**
   * Format date as short string
   *
   * Useful for compact displays (e.g., "Nov 15, 2025")
   *
   * @param {Date} date - Date to format
   * @returns {string} Short formatted date string
   */
  formatDateShort(date) {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  /**
   * Clear selection state
   *
   * No internal state to clear for single-day strategy.
   * All state is managed by the useCalendar hook.
   */
  clearSelection() {
    // No internal state to clear
  }

  /**
   * Get minimum booking date
   *
   * Utility function to get the earliest date that can be booked.
   * Calculates based on trip start time (e.g., 6 AM), not midnight.
   *
   * @returns {Date} Minimum bookable date (at midnight for calendar display)
   */
  getMinBookingDate() {
    const now = new Date();

    // Start with tomorrow and check each day
    const checkDate = new Date(now);
    checkDate.setDate(checkDate.getDate() + 1);
    checkDate.setHours(0, 0, 0, 0);

    // Check up to 5 days ahead to find first bookable date
    for (let i = 0; i < 5; i++) {
      const tripDateTime = new Date(checkDate);
      tripDateTime.setHours(this.startHour, this.startMinute, this.startSecond, 0);

      const hoursUntilTrip = (tripDateTime - now) / (1000 * 60 * 60);
      if (hoursUntilTrip >= this.minBookingHours) {
        return checkDate;
      }

      checkDate.setDate(checkDate.getDate() + 1);
    }

    // Fallback: return 2 days from now
    const fallback = new Date(now);
    fallback.setDate(fallback.getDate() + 2);
    fallback.setHours(0, 0, 0, 0);
    return fallback;
  }

  /**
   * Get maximum booking date (booking horizon)
   *
   * Utility function to get the latest date that can be booked.
   *
   * @returns {Date} Maximum bookable date
   */
  getMaxBookingDate() {
    const now = new Date();
    const maxDate = new Date(now);
    maxDate.setMonth(maxDate.getMonth() + this.bookingHorizonMonths);
    return maxDate;
  }

  /**
   * Check if a specific date is bookable (meets all constraints)
   *
   * Utility function to check if a date can be selected.
   * Does not check availability from API - only validates against booking rules.
   * Includes start day restriction check (MVP 1.4.2 SCRUM-407).
   *
   * @param {Date} date - Date to check
   * @returns {boolean} True if date meets booking constraints
   */
  isDateBookable(date) {
    const validation = this.validate([date]);
    if (!validation.isValid) {
      return false;
    }

    // Check start day restrictions (SCRUM-407)
    if (this.startDayRestrictions &&
        this.startDayRestrictions.allowed_days &&
        this.startDayRestrictions.allowed_days.length > 0) {
      const dayOfWeek = date.getDay();
      if (!this.startDayRestrictions.allowed_days.includes(dayOfWeek)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Get tooltip text for a disabled date
   * Returns reason text if the date is disabled due to start day restrictions
   *
   * @param {Date} date - Date to check
   * @returns {string|null} Tooltip text or null if no restriction applies
   */
  getDateDisabledReason(date) {
    if (this.startDayRestrictions &&
        this.startDayRestrictions.allowed_days &&
        this.startDayRestrictions.allowed_days.length > 0) {
      const dayOfWeek = date.getDay();
      if (!this.startDayRestrictions.allowed_days.includes(dayOfWeek)) {
        const dayNames = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'];
        const allowedNames = this.startDayRestrictions.allowed_days
          .map(d => dayNames[d])
          .join(' and ');
        return `This experience starts on ${allowedNames} only`;
      }
    }
    return null;
  }
}

export default SingleDayStrategy;
